import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const HarmReductionKitPage = () => {
  const navigate = useNavigate();

  const kitItems = [
    {
      category: 'Protection',
      items: [
        { name: 'Condoms', description: 'External and internal condoms for safer sex' },
        { name: 'Lube', description: 'Water or silicone-based lubricant' },
        { name: 'Gloves', description: 'Disposable gloves for safer handling' }
      ]
    },
    {
      category: 'Safer Use Supplies',
      items: [
        { name: 'Clean supplies', description: 'New, sterile supplies for each use' },
        { name: 'Alcohol swabs', description: 'For cleaning skin before use' },
        { name: 'Sterile water', description: 'For mixing/preparation' },
        { name: 'Cookers/spoons', description: 'Clean preparation tools' }
      ]
    },
    {
      category: 'Overdose Prevention',
      items: [
        { name: 'Naloxone/Narcan', description: 'Opioid overdose reversal medication - free from pharmacies' },
        { name: 'Fentanyl test strips', description: 'Test substances for fentanyl presence' }
      ]
    },
    {
      category: 'Health & Wellness',
      items: [
        { name: 'Water', description: 'Stay hydrated throughout' },
        { name: 'Snacks', description: 'Easy-to-eat food' },
        { name: 'Electrolytes', description: 'Sports drinks or electrolyte packets' },
        { name: 'Vitamins', description: 'Multivitamin or vitamin C' }
      ]
    },
    {
      category: 'Safety Items',
      items: [
        { name: 'Phone charger', description: 'Keep your phone charged' },
        { name: 'Emergency contacts', description: 'Written list of trusted contacts' },
        { name: 'ID', description: 'Keep identification accessible' },
        { name: 'First aid kit', description: 'Basic first aid supplies' }
      ]
    },
    {
      category: 'Comfort',
      items: [
        { name: 'Warm blanket', description: 'For temperature regulation' },
        { name: 'Tissues', description: 'For hygiene' },
        { name: 'Lip balm', description: 'Prevent dry lips' },
        { name: 'Eye drops', description: 'For comfort' }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <h1 className="text-2xl font-semibold tracking-tight text-[rgb(var(--text-primary))]">
              Harm Reduction Kit
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Intro */}
        <div className="mb-8 p-6 bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 rounded-2xl">
          <div className="flex items-start">
            <Package size={24} weight="duotone" className="text-[rgb(var(--primary))] mr-3 mt-1" />
            <div>
              <h2 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-2">
                Your Safer Party Kit
              </h2>
              <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed">
                Having these supplies ready helps you party safer. Check off what you have and what you need to get.
              </p>
            </div>
          </div>
        </div>

        {/* Kit Categories */}
        <div className="space-y-6" data-testid="harm-reduction-kit-list">
          {kitItems.map((category, catIndex) => (
            <div key={catIndex} className="space-y-3">
              <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))] uppercase tracking-wider">
                {category.category}
              </h3>
              <div className="space-y-2">
                {category.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    data-testid={`kit-item-${catIndex}-${itemIndex}`}
                    className="flex items-start p-4 bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-xl border border-white/5 transition-all duration-200"
                  >
                    <CheckCircle size={20} weight="duotone" className="text-[rgb(var(--primary))] mr-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-[rgb(var(--text-primary))] mb-1">
                        {item.name}
                      </h4>
                      <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Where to Get Supplies */}
        <div className="mt-8 space-y-4">
          <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))] uppercase tracking-wider">
            Where to Get Free Supplies in Ottawa
          </h3>
          <div className="space-y-3">
            <div className="p-4 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
              <h4 className="font-semibold text-[rgb(var(--text-primary))] mb-2">Max Ottawa</h4>
              <p className="text-sm text-[rgb(var(--text-secondary))]">
                Free condoms, lube, safer use supplies, and naloxone kits
              </p>
            </div>
            <div className="p-4 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
              <h4 className="font-semibold text-[rgb(var(--text-primary))] mb-2">Sandy Hill CHC</h4>
              <p className="text-sm text-[rgb(var(--text-secondary))]">
                Harm reduction supplies and support services
              </p>
            </div>
            <div className="p-4 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
              <h4 className="font-semibold text-[rgb(var(--text-primary))] mb-2">Pharmacies (Naloxone)</h4>
              <p className="text-sm text-[rgb(var(--text-secondary))]">
                Free naloxone kits available at most pharmacies - no prescription needed
              </p>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-8">
          <Button
            onClick={() => navigate('/resources')}
            data-testid="view-resources-button"
            className="w-full min-h-[56px] rounded-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/90 text-white font-semibold"
          >
            View All Ottawa Resources
          </Button>
        </div>
      </main>
    </div>
  );
};

export default HarmReductionKitPage;